import { useMemo, useState, useEffect } from 'react';
import { useWorkOrderCharges } from '@api/enterprise/workorder';
import { useOfflineDownloadService } from '@services/offlineDownloadService';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { subscribe } from '@services/offlineSyncService';
import type { WorkOrderChargeViewModel } from '@api/enterprise/openapi';

export const useWorkOrderChargesData = (jobNumber: string) => {
  const isOnline = useOnlineStatus();
  const { getCachedCharges } = useOfflineDownloadService();

  const [offlineCharges, setOfflineCharges] = useState<
    WorkOrderChargeViewModel[]
  >([]);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { data, isLoading, error } = useWorkOrderCharges(
    jobNumber,
    !!jobNumber && isOnline
  );

  useEffect(() => {
    if (!isOnline) {
      return subscribe(() => setRefreshKey(k => k + 1));
    }
  }, [isOnline]);

  useEffect(() => {
    const fetchOfflineCharges = async () => {
      if (!isOnline && jobNumber) {
        setOfflineLoading(true);
        try {
          const cached = await getCachedCharges(jobNumber);
          setOfflineCharges(cached);
        } catch {
          setOfflineCharges([]);
        } finally {
          setOfflineLoading(false);
        }
      }
    };

    fetchOfflineCharges();
  }, [isOnline, jobNumber, getCachedCharges, refreshKey]);

  const chargesData = isOnline ? data : offlineCharges;

  const charges = useMemo(() => {
    if (!chargesData) return [];

    return chargesData
      .filter(charge => charge.chargeType !== 'REPAIR PART')
      .map((charge: WorkOrderChargeViewModel) => ({
        jobNumber: charge.jobNumber,
        sequenceNumber: charge.sequenceNumber,
        chargeType: charge.chargeType,
        lineDescription: charge.lineDescription,
        lineQuantity: charge.lineQuantity,
        lineUnit: charge.lineUnit,
        lineCost: charge.lineCost,
        lineTotalCost: charge.lineTotalCost,
        lineType: charge.lineType?.toString(),
        branch: charge.branch,
        performedBy: charge.performedBy,
        enteredBy: charge.enteredBy,
        completedDate: charge.completedDate,
        actualCurrencyCode: charge.actualCurrencyCode,
        exchangeRate: charge.exchangeRate
      }));
  }, [chargesData]);

  return {
    charges,
    isLoading: isOnline ? isLoading : offlineLoading,
    error: isOnline ? error : null
  };
};
